<template>
  <div id="bandeja-requiere-atencion">
    <vx-card>
      <div class="gw-cabecera">
        <h2 class="gw-titulo">Requiere atención</h2>
        <p class="gw-bajada">
          Procesos que están a la espera de una acción para poder continuar.
          Se agrupan según quién debe actuar, de modo que puedas identificar
          cuáles dependen de tu equipo o del candidato.
        </p>
      </div>

      <!-- CARGANDO -->
      <div v-if="uiState === 'loading'" class="gw-centro">
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-centro-titulo">Cargando los procesos en espera…</div>
      </div>

      <!-- ERROR -->
      <div v-else-if="uiState === 'error'" class="gw-centro">
        <div class="gw-centro-titulo">No pudimos cargar la bandeja</div>
        <div class="gw-centro-texto">Vuelve a intentarlo en unos minutos.</div>
      </div>

      <!-- NADA QUE ATENDER: es una buena noticia, no un vacío -->
      <div v-else-if="total === 0" class="gw-despejado">
        <div class="gw-despejado-marca">✓</div>
        <h3 class="gw-despejado-titulo">No hay procesos en espera</h3>
        <p class="gw-despejado-texto">
          Ninguno de tus procesos está detenido a la espera de una acción.
          Cuando alguno lo esté, aparecerá aquí con el motivo y la fecha desde
          la que está en espera.
        </p>
      </div>

      <!-- LA BANDEJA -->
      <template v-else>
        <div
          v-for="grupo in gruposConFilas"
          :key="grupo.clave"
          class="gw-grupo"
        >
          <div class="gw-grupo-cabecera">
            <h3 class="gw-grupo-titulo">
              {{ grupo.titulo }}
              <span class="gw-grupo-conteo">{{ grupo.total }}</span>
            </h3>
            <p class="gw-grupo-ayuda">{{ grupo.ayuda }}</p>
          </div>

          <ul class="gw-filas">
            <li
              v-for="fila in grupo.filas"
              :key="fila.proceso_id + '-' + fila.validacion"
              class="gw-fila"
              @click="abrirProceso(fila.proceso_id)"
            >
              <div class="gw-fila-cuerpo">
                <!-- El titular: el motivo y desde cuándo. No la alarma. -->
                <div class="gw-fila-titular">
                  {{ fila.motivo }}
                  <span class="gw-fila-desde">· {{ desdeHace(fila.dias_esperando) }}</span>
                </div>
                <div class="gw-fila-detalle">
                  {{ fila.candidato }}
                  <template v-if="fila.cargo"> — {{ fila.cargo }}</template>
                  <span class="gw-fila-sep">·</span>
                  {{ etiquetaValidacion(fila.validacion) }}
                </div>
              </div>

              <!-- La urgencia, como dato secundario -->
              <div class="gw-fila-urgencia">
                <span :class="['gw-chip', 'gw-chip--' + tonoUrgencia(fila.urgencia.estado)]">
                  {{ etiquetaUrgencia(fila.urgencia.estado) }}
                </span>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </vx-card>
  </div>
</template>

<script>
import api from "../mixins/api";
import slaFormat from "../mixins/slaFormat";

export default {
  name: "AttentionInbox",
  mixins: [api, slaFormat],

  data() {
    return {
      uiState: "loading",
      data: null,
    };
  },

  computed: {
    total() {
      return this.data ? this.data.total : 0;
    },

    // Los grupos vacíos no se dibujan. En la pestaña de métricas enseñar el
    // hueco sirve, porque dice dónde va a aparecer la información; acá no:
    // la bandeja es para actuar, y un grupo vacío solo alarga el scroll.
    gruposConFilas() {
      if (!this.data) return [];
      return this.data.grupos.filter((g) => g.total > 0);
    },
  },

  methods: {
    abrirProceso(id) {
      this.$router.push({ path: "/project", query: { id: id } });
    },

    async initializeData() {
      this.uiState = "loading";
      const base = this.url().replace(/\/+$/, "");

      try {
        const request = await this.requestGetWithoutContent(
          base + "/api/v1/sla/attention_inbox",
          {}
        );

        if (!request || request.status !== 200 || !request.data) {
          this.uiState = "error";
          return;
        }

        this.data = request.data;
        this.uiState = "ready";
      } catch (e) {
        console.warn("[bandeja] no se pudo cargar:", e);
        this.uiState = "error";
      }
    },
  },

  mounted() {
    this.initializeData();
  },
};
</script>

<style scoped>
.gw-cabecera {
  margin-bottom: 26px;
}
.gw-titulo {
  font-size: 20px;
  font-weight: 700;
  color: #3b3b3b;
  margin: 0 0 6px;
}
.gw-bajada {
  font-size: 13px;
  line-height: 1.6;
  color: #6b6b6b;
  margin: 0;
  max-width: 76ch;
}

.gw-grupo {
  margin-bottom: 30px;
}
.gw-grupo-cabecera {
  margin-bottom: 10px;
}
.gw-grupo-titulo {
  font-size: 15px;
  font-weight: 700;
  color: #3b3b3b;
  margin: 0 0 2px;
  display: flex;
  align-items: center;
  gap: 9px;
}
.gw-grupo-conteo {
  font-size: 12px;
  font-weight: 600;
  color: #5f0091;
  background: rgba(95, 0, 145, 0.09);
  border-radius: 11px;
  padding: 2px 9px;
}
.gw-grupo-ayuda {
  font-size: 12px;
  color: #9a9a9a;
  margin: 0;
}

.gw-filas {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid #efefef;
  border-radius: 10px;
  overflow: hidden;
}
.gw-fila {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid #f4f4f4;
  cursor: pointer;
  transition: background 0.12s ease;
}
.gw-fila:last-child {
  border-bottom: none;
}
.gw-fila:hover {
  background: #faf7fc;
}
.gw-fila-cuerpo {
  flex: 1;
  min-width: 0;
}
.gw-fila-titular {
  font-size: 14px;
  color: #3b3b3b;
  font-weight: 600;
  line-height: 1.4;
}
.gw-fila-desde {
  font-weight: 400;
  color: #6b6b6b;
}
.gw-fila-detalle {
  font-size: 12px;
  color: #9a9a9a;
  margin-top: 3px;
}
.gw-fila-sep {
  margin: 0 5px;
}
.gw-fila-urgencia {
  flex-shrink: 0;
}

.gw-chip {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 11px;
  white-space: nowrap;
}
.gw-chip--neutro {
  background: #f2f2f2;
  color: #8a8a8a;
}
.gw-chip--ok {
  background: #eaf5ef;
  color: #2e7d52;
}
.gw-chip--aviso {
  background: #fdf4e3;
  color: #9a6d12;
}
.gw-chip--alerta {
  background: #f6eceb;
  color: #a0503f;
}

.gw-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 16px;
  text-align: center;
}
.gw-centro-titulo {
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}
.gw-centro-texto {
  font-size: 12px;
  color: #9a9a9a;
}
.gw-spinner {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95, 0, 145, 0.18);
  border-top-color: rgba(95, 0, 145, 0.9);
  animation: gwspin 0.9s linear infinite;
}
@keyframes gwspin {
  to {
    transform: rotate(360deg);
  }
}

.gw-despejado {
  max-width: 520px;
  margin: 18px auto 24px;
  padding: 34px 28px;
  text-align: center;
  background: #f7fbf8;
  border: 1px solid rgba(46, 125, 82, 0.14);
  border-radius: 14px;
}
.gw-despejado-marca {
  width: 48px;
  height: 48px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: rgba(46, 125, 82, 0.1);
  color: #2e7d52;
  font-size: 22px;
  line-height: 48px;
}
.gw-despejado-titulo {
  font-size: 17px;
  font-weight: 700;
  color: #3b3b3b;
  margin: 0 0 8px;
}
.gw-despejado-texto {
  font-size: 13px;
  line-height: 1.6;
  color: #6b6b6b;
  margin: 0 auto;
  max-width: 48ch;
}
</style>
